import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Chat-style auto-scroll (2026-07-18 requirement): the container scrolls
 * freely by hand, but while the reader is "following" (at/near the bottom),
 * new text keeps scrolling into view. The content carries ~20vh of bottom
 * padding (CSS), so the newest line floats about a fifth of the screen
 * above the container's bottom edge instead of sticking to it.
 */
export function useFollowScroll(ref: RefObject<HTMLElement | null>, dep: number): void {
  const following = useRef(true);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
      following.current = distanceFromBottom < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [ref]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && following.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [ref, dep]);
}
